import * as React from "react";
import { Copy, ChevronLeft, ChevronRight, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  fetchPlanEnrollments,
  type PlanEnrollmentStudentRow,
} from "@/lib/api/subscription-plan-stats";
import { formatAdminBusinessEt } from "@/lib/admin-datetime";

type PlanEnrollmentsSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  squarePlanId: string | null;
  planLabel?: string | null;
  installmentAmount?: number | null;
};

const PER_PAGE = 25;

export function PlanEnrollmentsSheet({
  open,
  onOpenChange,
  squarePlanId,
  planLabel,
  installmentAmount,
}: PlanEnrollmentsSheetProps) {
  const [rows, setRows] = React.useState<PlanEnrollmentStudentRow[]>([]);
  const [page, setPage] = React.useState(1);
  const [pages, setPages] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  const loadPage = React.useCallback(
    async (targetPage: number) => {
      if (!squarePlanId) return;
      setLoading(true);
      try {
        const data = await fetchPlanEnrollments(squarePlanId, targetPage, PER_PAGE);
        setRows(data.enrollments);
        setPage(data.pagination.page);
        setPages(data.pagination.pages);
        setTotal(data.pagination.total);
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to load enrolled students"
        );
        setRows([]);
        setPages(0);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [squarePlanId]
  );

  React.useEffect(() => {
    if (!open || !squarePlanId) return;
    setPage(1);
    void loadPage(1);
  }, [open, squarePlanId, loadPage]);

  const handleCopyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success("Email copied");
    } catch {
      toast.error("Could not copy email");
    }
  };

  const title = planLabel?.trim() || "Subscription plan";
  const amountLabel =
    installmentAmount != null
      ? `$${installmentAmount.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}/payment`
      : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-xl"
      >
        {/* Header */}
        <div className="shrink-0 border-b border-border bg-background px-6 pb-5 pt-6 pr-14">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <SheetTitle className="text-lg font-semibold leading-tight">
                Enrolled students
              </SheetTitle>
              <SheetDescription asChild>
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-foreground">
                    {title}
                    {amountLabel ? (
                      <span className="font-normal text-muted-foreground">
                        {" "}
                        · {amountLabel}
                      </span>
                    ) : null}
                  </p>
                  {squarePlanId ? (
                    <p
                      className="break-all rounded-md bg-muted/60 px-2.5 py-1.5 font-mono text-[11px] leading-relaxed text-muted-foreground"
                      title={squarePlanId}
                    >
                      {squarePlanId}
                    </p>
                  ) : null}
                </div>
              </SheetDescription>
              <Badge variant="secondary" className="font-normal">
                {total} subscription enrollment{total === 1 ? "" : "s"}
              </Badge>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex min-h-0 flex-1 flex-col px-6 py-5">
          <div
            className={cn(
              "flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm"
            )}
          >
            {loading ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-20 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
                <p className="text-sm">Loading students…</p>
              </div>
            ) : rows.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-20 text-center">
                <Users className="h-8 w-8 text-muted-foreground/50" />
                <p className="text-sm font-medium text-foreground">
                  No enrolled students
                </p>
                <p className="max-w-[240px] text-sm text-muted-foreground">
                  No subscription enrollments were found for this plan.
                </p>
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Student
                      </TableHead>
                      <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Program
                      </TableHead>
                      <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Enrolled
                      </TableHead>
                      <TableHead className="h-11 w-12 px-2" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.orderId} className="hover:bg-muted/40">
                        <TableCell className="px-4 py-4 align-top">
                          <div className="space-y-0.5">
                            <p className="font-medium leading-snug text-foreground">
                              {row.student.fullName || "—"}
                            </p>
                            <p className="text-xs leading-relaxed text-muted-foreground">
                              {row.student.email}
                            </p>
                            {row.student.cellPhoneNumber ? (
                              <p className="text-xs text-muted-foreground">
                                {row.student.cellPhoneNumber}
                              </p>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-4 align-top">
                          <div className="space-y-0.5">
                            <p className="text-sm leading-snug text-foreground">
                              {row.programName || row.program || "—"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {row.deliveryMode}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-4 align-top text-sm whitespace-nowrap text-foreground">
                          {formatAdminBusinessEt(row.enrolledAt)}
                        </TableCell>
                        <TableCell className="px-2 py-4 align-top">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            aria-label={`Copy email for ${row.student.fullName || row.student.email}`}
                            onClick={() => void handleCopyEmail(row.student.email)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        {/* Footer pagination */}
        {pages > 1 ? (
          <div className="shrink-0 border-t border-border bg-muted/30 px-6 py-4">
            <div className="flex items-center justify-between gap-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-w-[88px]"
                disabled={page <= 1 || loading}
                onClick={() => void loadPage(page - 1)}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm tabular-nums text-muted-foreground">
                Page {page} of {pages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-w-[88px]"
                disabled={page >= pages || loading}
                onClick={() => void loadPage(page + 1)}
              >
                Next
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="shrink-0 border-t border-border px-6 py-3" aria-hidden />
        )}
      </SheetContent>
    </Sheet>
  );
}
